import { IGlobalStore } from 'src/types/store'
import {
  TStateCommunications,
  createStateSagaCommunications,
} from 'src/helpers/redux'
import { ICall } from 'src/store/modules/communication/types'
import { actions as sagaActions, PREFIX } from './actions'

export type DeviceStatus =
  | 'uninitialized'
  | 'registering'
  | 'registered'
  | 'unregistered'
  | 'error'

export type CallPhase =
  | 'idle' // no call
  | 'incoming' // ringing for us: accept / reject
  | 'dialing' // media is being set up: connect() is made (no ringing yet) or incoming is accepted
  | 'ringing' // ringing on the other side
  | 'connected' // conversation
  | 'reconnecting' // connection lost, SDK is reconnecting

/** Someone on the other end of the call. There may be several of them (conference) later. */
export interface ICallParticipant {
  number: string
  name?: string
  secondary?: string
  avatarUrl?: string
  /** Storage object, if the number is bound to one */
  scheme?: string
  uniqId?: string
}

/** Raw participant from CallResource. Not the store participant shown in the UI */
export interface IBackendCallParticipant {
  id: number
  kind: 'customer' | 'user' | 'fallback'
  purpose: string | null
  inviteKind: 'origin' | 'ring' | 'transfer' | 'add' | null
  userId: number | null
  callSid: string | null
  status:
    | 'initiated'
    | 'ringing'
    | 'connected'
    | 'left'
    | 'no_answer'
    | 'busy'
    | 'failed'
    | 'cancelled'
  onHold: boolean
  isPrimary: boolean
  answeredAt: string | null
  endedAt: string | null
  duration: number | null
  userData?: {
    id: number
    identifier: string
    info?: {
      firstName?: string | null
      lastName?: string | null
      avatar?: string | null
    }
  } | null
}

export type TTransferMode = 'blind' | 'warm'

export type TTransferStatus =
  | 'ringing'
  | 'consulting'
  | 'completed'
  | 'failed'
  | 'cancelled'

export type TTransferFailureReason =
  | 'no_answer'
  | 'busy'
  | 'failed'
  | 'cancelled'
  | 'target_left'
  | null

/** Transfer of the current call. Read from `backendCall.transfer`, not a separate store */
export interface ICallTransfer {
  status: TTransferStatus
  mode: TTransferMode
  initiatorUserId: number
  targetUserId: number
  targetParticipantId: number
  failureReason: TTransferFailureReason
  startedAt: string
  endedAt: string | null
}

/** Calls already loaded for one storage object */
export interface ISubscriberCallHistory {
  storageUniqId: string
  data: ICall[]
}

/** Backend call found by the Twilio CallSid. The two ids are different */
export interface IBackendCall {
  id: number
  mode: 'dial' | 'hybrid' | 'conference'
  escalationStatus: string | null
  onHold: boolean
  revision: number
  transfer?: ICallTransfer | null
  /** Raw backend participants. Call logic reads these, not `ICallParticipant` */
  participants?: IBackendCallParticipant[]
}

export interface IState {
  communication: TStateCommunications<typeof sagaActions>
  data: {
    device: {
      status: DeviceStatus
      error?: string
    }
    call: {
      phase: CallPhase
      muted: boolean
      /** Timestamp of the answer, for the call timer */
      acceptedAt: number | null
    }
    /** Twilio CallSid of the current call, used only to find the backend call */
    callSid: string | null
    /** Backend call of the current Twilio call. Its id is used by control endpoints */
    backendCall: IBackendCall | null
    participants: ICallParticipant[]
    /** Call history keyed by the storage uniq id it was requested with */
    subscriberCallHistory: Record<string, ISubscriberCallHistory>
    /** `storageUniqId` of the history request in flight */
    subscriberCallHistoryPendingUniqId: string | null
    /** `storageUniqId` whose last history request failed */
    subscriberCallHistoryErrorUniqId: string | null
  }
}

export const initialCall: IState['data']['call'] = {
  phase: 'idle',
  muted: false,
  acceptedAt: null,
}

export const initialState: IState = {
  communication: createStateSagaCommunications(sagaActions),
  data: {
    device: { status: 'uninitialized' },
    call: initialCall,
    callSid: null,
    backendCall: null,
    participants: [],
    subscriberCallHistory: {},
    subscriberCallHistoryPendingUniqId: null,
    subscriberCallHistoryErrorUniqId: null,
  },
}

export type TGlobalStateWithService = IGlobalStore & {
  [PREFIX]: IState
}

export { PREFIX }
